import GeoJSONFormat from 'ol/format/GeoJSON';
import type FeatureLike from 'ol/Feature';
import { Draw, DragBox, Snap } from 'ol/interaction';
import { createBox } from 'ol/interaction/Draw';
import type BaseLayer from 'ol/layer/Base';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import Map from 'ol/Map';
import { unByKey } from 'ol/Observable';
import { fromLonLat, toLonLat } from 'ol/proj';
import Cluster from 'ol/source/Cluster';
import TileWMS from 'ol/source/TileWMS';
import VectorSource from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';
import CircleGeom from 'ol/geom/Circle';
import type SimpleGeometry from 'ol/geom/SimpleGeometry';
import { createEmpty, extend, getHeight, getWidth, isEmpty as isExtentEmpty } from 'ol/extent';
import { getArea, getLength } from 'ol/sphere';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Icon from 'ol/style/Icon';
import Stroke from 'ol/style/Stroke';
import Style, { type StyleLike } from 'ol/style/Style';
import Text from 'ol/style/Text';
import View from 'ol/View';
import Overlay from 'ol/Overlay';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useAdminData } from '../admin/AdminDataContext';
import { pickObjectTitle } from '../components/shared/useObjectCard';
import { ApiError } from '../lib/api';
import { layersApi } from '../lib/layersApi';
import type { DemoFeatureProps, LayerConfig } from './demoLayers';
import { buildBasemapLayer, buildWmtsLayer, createFallbackBasemapLayer, pickInitialBasemap } from './basemaps';
import { clusterGeometryFunction, matchesAttributeValue, resolveLayerStyle, resolveMarkerStyle } from './styleResolve';

const FALLBACK_CENTER: [number, number] = [37.6173, 55.7558];

type GeoJsonGeometryLike = { type: string; coordinates?: unknown };

/** Layer geometry_type (backend code) -> the OL Draw interaction type it maps to.
 * Only layers with one of these concrete types can be drawn into — 'mixed' (only
 * ever produced by file import) and raster/none layers are excluded on purpose. */
const OL_DRAW_TYPE: Record<string, 'Point' | 'MultiPoint' | 'LineString' | 'MultiLineString' | 'Polygon' | 'MultiPolygon'> = {
  point: 'Point',
  multipoint: 'MultiPoint',
  line: 'LineString',
  multiline: 'MultiLineString',
  polygon: 'Polygon',
  multipolygon: 'MultiPolygon',
};

export type MeasureMode = 'distance' | 'area' | 'perimeter';
export type MeasureShape = 'polygon' | 'circle' | 'rectangle';

/** One field==value check, applied per layer — see LayersDrawer's "Фильтр по
 * полю". Objects that don't match stay in the source (so counts/exports are
 * unaffected) but render with no style, which also makes them unclickable
 * (same mechanism as the cluster "grouped away" hide, see the vector layer
 * style functions below). */
export type AttributeFilter = { fieldCode: string; value: string };

export type SelectionResult = {
  id: string;
  layerId: string;
  layerName: string;
  properties: DemoFeatureProps;
  photoFileId: string | null;
};

/** One item accumulated by the map's "Объектный калькулятор" tool (see
 * startObjectCalc) — a running tally across several same-layer objects clicked one at
 * a time, distinct from the existing per-object calculator toggle in
 * SelectedObjectCard/ObjectCardModal, which only ever shows metrics for whichever
 * single object is currently open. */
export type ObjectCalcSelection = {
  id: string;
  layerId: string;
  layerName: string;
  title: string;
  metrics: SelectedFeatureMetrics;
};

export type SelectedFeatureMetrics =
  | { kind: 'point'; lon: number; lat: number }
  | { kind: 'line'; lengthM: number; start: [number, number]; end: [number, number] }
  | { kind: 'polygon'; areaM2: number; perimeterM: number; vertices: [number, number][] }
  | null;

type MapContextValue = {
  containerRef: RefObject<HTMLDivElement | null>;
  ready: boolean;
  layers: LayerConfig[];
  layerVisibility: Record<string, boolean>;
  layerOpacity: Record<string, number>;
  setLayerVisible: (id: string, visible: boolean) => void;
  setLayerOpacity: (id: string, opacity: number) => void;
  selectedFeature: DemoFeatureProps | null;
  selectedFeatureId: string | null;
  selectedFeatureLayerId: string | null;
  selectedFeaturePhotoFileId: string | null;
  selectedFeatureMetrics: SelectedFeatureMetrics;
  clearSelection: () => void;
  deleteSelectedFeature: () => Promise<void>;
  activeBasemapId: string | null;
  setBasemap: (id: string) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  locate: () => void;
  locating: boolean;
  locateError: string | null;
  clearLocateError: () => void;
  measureActive: boolean;
  measureMode: MeasureMode | null;
  measureShape: MeasureShape | null;
  startMeasure: (mode: MeasureMode, shape?: MeasureShape) => void;
  stopMeasure: () => void;
  boxSelectActive: boolean;
  startBoxSelect: (scope: 'all' | 'active') => void;
  stopBoxSelect: () => void;
  spatialQueryActive: boolean;
  startSpatialQuery: () => void;
  stopSpatialQuery: () => void;
  aiAreaSelectActive: boolean;
  aiAreaGeometry: { type: string; coordinates: unknown } | null;
  startAiAreaSelect: () => void;
  stopAiAreaSelect: () => void;
  clearAiAreaGeometry: () => void;
  selectionResults: SelectionResult[];
  selectionIndex: number;
  setSelectionIndex: (i: number) => void;
  clearSelectionResults: () => void;
  deleteSelectionResults: () => Promise<{ deleted: number; failed: number }>;
  objectCalculatorEnabled: boolean;
  toggleObjectCalculator: () => void;
  /** The map-toolbar tool: click same-type objects one at a time to build a running
   * tally (count + summed length/area) — see ObjectCalcSelection. Prototype feature,
   * gated at its one entry point (MapControls' button) by
   * featureFlags.objectCalculatorOnMap — this state/logic layer has no flag check of
   * its own since it's unreachable without that button. */
  objectCalcActive: boolean;
  objectCalcItems: ObjectCalcSelection[];
  objectCalcTotals: { count: number; lengthM: number | null; areaM2: number | null };
  startObjectCalc: () => void;
  stopObjectCalc: () => void;
  clearObjectCalc: () => void;
  drawActive: boolean;
  drawLayerId: string | null;
  drawError: string | null;
  startDraw: (layerId: string) => void;
  stopDraw: () => void;
  /** The click-to-place-a-new-object interaction, separate from drawActive
   * (the editing session itself) — a plain click while just browsing/selecting
   * objects inside that session never silently creates a duplicate; adding a
   * new one is this explicit second toggle. */
  addObjectActive: boolean;
  startAddObject: () => void;
  stopAddObject: () => void;
  addObjectAtCoordinate: (lon: number, lat: number) => void;
  attributeFilters: Record<string, AttributeFilter | null>;
  setAttributeFilter: (layerId: string, filter: AttributeFilter | null) => void;
  removeFeatureFromMap: (layerId: string, featureId: string) => void;
  coordinate: [number, number] | null;
  zoomLevel: number;
  scaleLabel: string;
  fitToLayers: () => void;
  fitToLayerExtent: (layerId: string) => void;
  flyToSearchHit: (geometry: GeoJsonGeometryLike, properties: DemoFeatureProps) => void;
  flyToCoordinates: (lon: number, lat: number) => void;
  getViewState: () => { lon: number; lat: number; zoom: number } | null;
  openTableLayer: { id: string; name: string } | null;
  openLayerTable: (id: string, name: string) => void;
  closeLayerTable: () => void;
  /** Populated instead of the normal cluster fit-to-extent when a click hits 2+
   * features at the exact same coordinate — see pickOverlapItemTitle above. */
  overlapPicker: { pixel: [number, number]; items: OverlapPickItem[] } | null;
  selectOverlapItem: (item: OverlapPickItem) => void;
  closeOverlapPicker: () => void;
};

const MapContext = createContext<MapContextValue | null>(null);

export function useMapContext(): MapContextValue {
  const ctx = useContext(MapContext);
  if (!ctx) throw new Error('useMapContext must be used within <MapProvider>');
  return ctx;
}

/** Same context, without the throw — for components (like LayerTable) that are
 * reused on standalone pages with no map mounted and just skip map-sync calls there. */
export function useMapContextOptional(): MapContextValue | null {
  return useContext(MapContext);
}

const CLICKABLE_GEOMETRY_TYPES = new Set([
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
]);

function getFeatureProps(feature: FeatureLike, cfg?: LayerConfig): DemoFeatureProps {
  const { geometry: _geometry, ...rest } = feature.getProperties();
  if (!cfg?.popupFieldCodes) return rest as DemoFeatureProps;
  const allowed = new Set(cfg.popupFieldCodes);
  const filtered: DemoFeatureProps = {};
  for (const [key, value] of Object.entries(rest)) {
    if (allowed.has(key)) filtered[key] = value;
  }
  return filtered;
}

/** Line -> length + endpoints, polygon -> area + perimeter + corner coordinates —
 * feeds the "Объектный калькулятор" tool. Only computed for direct map-click
 * selection (real feature geometry), not for search hits or box-select paging. */

/** One entry in the "выберите объект" list shown when a click hits several
 * features stacked at the exact same coordinate (e.g. several monthly meter
 * readings at one physical meter) — see the degenerate-extent branch below.
 * Zooming in can never separate exactly-coincident points, so fit()-to-cluster
 * would be a dead end; this gives the user an explicit way to pick one. */
export type OverlapPickItem = { key: string; layerId: string; layerName: string; title: string; feature: FeatureLike };

function pickOverlapItemTitle(props: DemoFeatureProps): string {
  const base = pickObjectTitle(props);
  const date = typeof props.reading_date === 'string' ? props.reading_date : null;
  return date && !base.includes(date) ? `${base} — ${date}` : base;
}

function computeFeatureMetrics(geom: SimpleGeometry | undefined | null): SelectedFeatureMetrics {
  if (!geom) return null;
  const type = geom.getType();
  if (type === 'Point') {
    const coord = (geom as unknown as { getCoordinates: () => number[] }).getCoordinates();
    const [lon, lat] = toLonLat(coord) as [number, number];
    return { kind: 'point', lon, lat };
  }
  if (type === 'MultiPoint') {
    const coords = (geom as unknown as { getCoordinates: () => number[][] }).getCoordinates();
    if (coords.length === 0) return null;
    const [lon, lat] = toLonLat(coords[0]) as [number, number];
    return { kind: 'point', lon, lat };
  }
  if (type === 'LineString' || type === 'MultiLineString') {
    const lengthM = getLength(geom);
    const coords: number[][] =
      type === 'LineString'
        ? (geom as unknown as { getCoordinates: () => number[][] }).getCoordinates()
        : (geom as unknown as { getCoordinates: () => number[][][] }).getCoordinates().flat();
    if (coords.length < 2) return null;
    const start = toLonLat(coords[0]) as [number, number];
    const end = toLonLat(coords[coords.length - 1]) as [number, number];
    return { kind: 'line', lengthM, start, end };
  }
  if (type === 'Polygon' || type === 'MultiPolygon') {
    const areaM2 = getArea(geom);
    const perimeterM = getLength(geom);
    const ring: number[][] =
      type === 'Polygon'
        ? (geom as unknown as { getCoordinates: () => number[][][] }).getCoordinates()[0]
        : (geom as unknown as { getCoordinates: () => number[][][][] }).getCoordinates()[0][0];
    const vertices = ring.slice(0, -1).map((c) => toLonLat(c) as [number, number]);
    return { kind: 'polygon', areaM2, perimeterM, vertices };
  }
  return null;
}

/** A little pushpin/thumbtack marker for a manually entered coordinate — a plain
 * dot didn't feel like landing "on" a spot, this reads as pinned to the map. */
const PIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
  <ellipse cx="16" cy="36" rx="4" ry="1.5" fill="#00000030"/>
  <line x1="16" y1="18" x2="16" y2="35" stroke="#78716c" stroke-width="2" stroke-linecap="round"/>
  <circle cx="16" cy="11" r="10" fill="#ef4444" stroke="#ffffff" stroke-width="2"/>
  <circle cx="12.5" cy="7.5" r="2.8" fill="#ffffff" opacity="0.55"/>
</svg>`;
const PIN_ICON_SRC = `data:image/svg+xml,${encodeURIComponent(PIN_SVG)}`;
let pinStyleCache: Style | null = null;
function pinStyle(): Style {
  if (!pinStyleCache) {
    pinStyleCache = new Style({
      image: new Icon({ src: PIN_ICON_SRC, anchor: [0.5, 0.875], scale: 1 }),
    });
  }
  return pinStyleCache;
}

/** A solid-color bubble (one slice = every visible member is on the same
 * layer) or a donut sliced proportionally by layer (2+ layers among the
 * cluster's currently-visible members) — either way labeled with the total
 * count. Only ever called for an actual 2+ member cluster; a size-1 "cluster"
 * renders with its real geometry/style on the direct layer instead (see the
 * vector layer setup below), so drawing a bubble for it too would double it up. */
function clusterPieBubbleStyle(slices: { color: string; count: number }[]): Style {
  const total = slices.reduce((sum, s) => sum + s.count, 0);
  const radius = Math.min(26, 12 + Math.sqrt(total) * 3);
  if (slices.length <= 1) {
    return new Style({
      image: new CircleStyle({
        radius,
        fill: new Fill({ color: `${slices[0]?.color ?? '#2563eb'}dd` }),
        stroke: new Stroke({ color: '#ffffff', width: 2 }),
      }),
      text: new Text({
        text: String(total),
        fill: new Fill({ color: '#ffffff' }),
        font: '600 12px Inter, sans-serif',
      }),
    });
  }
  // Multiple layers in one cluster — no single `fill`/`stroke`/`image` can draw
  // a pie, so this draws directly on the layer renderer's own canvas instead
  // (see https://openlayers.org/en/latest/examples/cluster-pie-chart.html, the
  // same technique). pixelCoordinates is already in canvas-pixel space for a
  // Point style; sizes must be scaled by pixelRatio manually since a custom
  // renderer bypasses OL's usual automatic retina handling.
  return new Style({
    renderer: (pixelCoordinates, state) => {
      const [x, y] = pixelCoordinates as [number, number];
      const ctx = state.context;
      const ratio = state.pixelRatio;
      let angle = -Math.PI / 2;
      for (const slice of slices) {
        const sweep = (slice.count / total) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.arc(x, y, radius * ratio, angle, angle + sweep);
        ctx.closePath();
        ctx.fillStyle = slice.color;
        ctx.fill();
        angle += sweep;
      }
      ctx.beginPath();
      ctx.arc(x, y, radius * ratio, 0, Math.PI * 2);
      ctx.lineWidth = 2 * ratio;
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.font = `600 ${12 * ratio}px Inter, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(total), x, y);
    },
    // A custom `renderer` draws with colors IT picks (slice.color above), so OL
    // can't substitute its own per-feature hit-test color into that drawing —
    // that's exactly why clicking these pie bubbles was unreliable ("works in
    // one place, not in another": solid single-layer bubbles use plain
    // CircleStyle, which OL hit-tests natively; only the pie-chart ones went
    // through this custom path). hitDetectionRenderer draws the same full
    // circle but calls `ctx.fill()` without touching fillStyle, so OL's own
    // hit-color (already set on the context before this runs) is what
    // actually gets drawn into the hit-test buffer.
    hitDetectionRenderer: (pixelCoordinates, state) => {
      const [x, y] = pixelCoordinates as [number, number];
      const ctx = state.context;
      const ratio = state.pixelRatio;
      ctx.beginPath();
      ctx.arc(x, y, radius * ratio, 0, Math.PI * 2);
      ctx.fill();
    },
  });
}

/** Web Mercator inflates ground distance away from the equator — correct by cos(lat)
 * before converting resolution (m/px) to a conventional cartographic scale ratio,
 * using the standard 0.0254m/96px screen pixel size. */
function formatScaleLabel(resolutionMetersPerPixel: number, latDeg: number): string {
  const groundResolution = resolutionMetersPerPixel * Math.cos((latDeg * Math.PI) / 180);
  const scaleDenominator = (groundResolution * 96) / 0.0254;
  return `1:${Math.round(scaleDenominator).toLocaleString('ru-RU')}`;
}

/** Rosreestr's public cadastral overlays (NSPD) — not user-managed gis.layers
 * rows, just two fixed WMS sources merged into `layers` whenever tools.
 * rosreestr is on, so they flow through the exact same wmsLayers/
 * layerVisibility/layerOpacity machinery every other WMS layer already uses
 * (LayersDrawer shows them with a normal visibility checkbox + opacity
 * slider, defaulting to off — the tool toggle only controls whether they
 * exist at all, not whether they're shown). isReal is left unset so
 * LayersDrawer's ⋮ menu (edit/table/move-to-group — all meaningless for a
 * hardcoded external layer) doesn't render for them. Goes through this app's
 * own nginx (/nspd/ → nspd.gov.ru, see nginx.conf) rather than the browser
 * hitting nspd.gov.ru directly — see that file's comment for why. */
const ROSREESTR_LAYERS: LayerConfig[] = [
  {
    id: '__rosreestr_parcels__',
    name: 'Роскадастр: границы участков',
    color: '#dc2626',
    opacity: 1,
    count: 0,
    defaultVisible: false,
    wms: { url: '/nspd/v4/36048/wms', layer: '36048', version: '1.3.0', format: 'image/png' },
    status: 'published',
  },
  {
    id: '__rosreestr_buildings__',
    name: 'Роскадастр: здания',
    color: '#0891b2',
    opacity: 1,
    count: 0,
    defaultVisible: false,
    wms: { url: '/nspd/v3/36049/wms', layer: '36049', version: '1.3.0', format: 'image/png' },
    status: 'published',
  },
];

export function MapProvider({
  children,
  initialRealLayers = [],
}: {
  children: ReactNode;
  /** Layers with real geometry, already fetched (see map/realLayers.ts) — passed in
   * so the map's one-time mount effect below has them from the start, avoiding the
   * complexity of adding OL layers dynamically after mount. */
  initialRealLayers?: LayerConfig[];
}) {
  const { mapSettings, basemaps: adminBasemaps } = useAdminData();

  const center: [number, number] = mapSettings
    ? [mapSettings.initialView.centerLon, mapSettings.initialView.centerLat]
    : FALLBACK_CENTER;
  const initialZoom = mapSettings?.initialView.zoom ?? 13;
  const mapCenter = useMemo(() => fromLonLat(center), [center]);
  const clusteringEnabled = mapSettings?.tools?.clustering !== false;

  const layers = useMemo(
    () => (mapSettings?.tools?.rosreestr ? [...initialRealLayers, ...ROSREESTR_LAYERS] : initialRealLayers),
    [initialRealLayers, mapSettings?.tools?.rosreestr],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  const vectorSourcesRef = useRef<Record<string, VectorSource>>({});
  const vectorLayersRef = useRef<Record<string, BaseLayer>>({});
  // The one shared cross-layer cluster bubble layer (see the vector layer
  // setup below) — kept separately from vectorLayersRef (which is per-layer)
  // so setLayerVisible/setAttributeFilter can force it to re-style without
  // needing to know which layers currently have points clustered into it.
  const sharedClusterLayerRef = useRef<BaseLayer | null>(null);
  // The shared cluster's own underlying source — a lazy layer's viewport
  // refresh (see that effect further down) swaps its per-layer source's
  // features out on every pan/zoom and needs to keep this one in sync, or its
  // points would only ever cluster with themselves, never with other layers.
  const clusterableSourceRef = useRef<VectorSource | null>(null);
  // A ref (not just the attributeFilters state) so the per-feature style
  // functions below — closed over once when the vector layers are built —
  // always read the current filter without needing every layer rebuilt on
  // every filter change (see setAttributeFilter).
  const attributeFiltersRef = useRef<Record<string, AttributeFilter | null>>({});
  const tileLayersRef = useRef<Record<string, BaseLayer>>({});
  const selectedFeatureRef = useRef<FeatureLike | null>(null);
  const measureSourceRef = useRef<VectorSource | null>(null);
  const measureInteractionRef = useRef<Draw | null>(null);
  const measureTooltipRef = useRef<Overlay | null>(null);
  const searchHighlightSourceRef = useRef<VectorSource | null>(null);
  const drawInteractionRef = useRef<Draw | null>(null);
  const snapInteractionRef = useRef<Snap | null>(null);
  const dragBoxRef = useRef<DragBox | null>(null);
  // Last box drawn in scope='active' box-select — deleteSelectionResults uses this
  // to delete-by-area server-side instead of by the (possibly capped) id list in
  // selectionResults. See startBoxSelect's 'active' branch and deleteSelectionResults.
  const activeBoxExtentRef = useRef<{ mapExtent: number[]; bbox: [number, number, number, number] } | null>(null);
  const spatialQueryInteractionRef = useRef<Draw | null>(null);
  const aiAreaInteractionRef = useRef<Draw | null>(null);

  const [ready, setReady] = useState(false);
  const [layerVisibility, setLayerVisibilityState] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(layers.map((l) => [l.id, l.defaultVisible])),
  );
  // Mirrors layerVisibility for the cross-layer cluster style function below,
  // which — like attributeFiltersRef — is closed over once and must read the
  // current value on every call rather than a stale one from when the vector
  // layers were (re)built.
  const layerVisibilityRef = useRef<Record<string, boolean>>(
    Object.fromEntries(layers.map((l) => [l.id, l.defaultVisible])),
  );
  const [layerOpacity, setLayerOpacityState] = useState<Record<string, number>>(() =>
    Object.fromEntries(layers.map((l) => [l.id, l.opacity ?? 1])),
  );
  const [selectedFeature, setSelectedFeature] = useState<DemoFeatureProps | null>(null);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [selectedFeatureLayerId, setSelectedFeatureLayerId] = useState<string | null>(null);
  const [selectedFeaturePhotoFileId, setSelectedFeaturePhotoFileId] = useState<string | null>(null);
  const [selectedFeatureMetrics, setSelectedFeatureMetrics] = useState<SelectedFeatureMetrics>(null);
  const [activeBasemapId, setActiveBasemapId] = useState<string | null>(
    () => pickInitialBasemap(adminBasemaps, mapSettings?.defaultBasemapName)?.id ?? null,
  );
  const [measureActive, setMeasureActive] = useState(false);
  const [measureMode, setMeasureMode] = useState<MeasureMode | null>(null);
  const [measureShape, setMeasureShape] = useState<MeasureShape | null>(null);
  const [boxSelectActive, setBoxSelectActive] = useState(false);
  const [spatialQueryActive, setSpatialQueryActive] = useState(false);
  const [aiAreaSelectActive, setAiAreaSelectActive] = useState(false);
  const [aiAreaGeometry, setAiAreaGeometry] = useState<{ type: string; coordinates: unknown } | null>(null);
  const [selectionResults, setSelectionResults] = useState<SelectionResult[]>([]);
  const [selectionIndex, setSelectionIndexState] = useState(0);
  const [objectCalculatorEnabled, setObjectCalculatorEnabled] = useState(false);
  const [objectCalcActive, setObjectCalcActive] = useState(false);
  const [objectCalcItems, setObjectCalcItems] = useState<ObjectCalcSelection[]>([]);
  // The click handler below lives inside the map's one-time mount effect ([] deps) —
  // it closes over state from that first render, so a plain `objectCalcActive` read in
  // there would always see `false`. This ref is kept in sync via the effect right after
  // it and is what the click handler actually checks.
  const objectCalcActiveRef = useRef(false);
  const [overlapPicker, setOverlapPicker] = useState<{ pixel: [number, number]; items: OverlapPickItem[] } | null>(null);
  const [drawActive, setDrawActive] = useState(false);
  const [drawLayerId, setDrawLayerId] = useState<string | null>(null);
  const [drawError, setDrawError] = useState<string | null>(null);
  const [addObjectActive, setAddObjectActive] = useState(false);
  const [attributeFilters, setAttributeFilters] = useState<Record<string, AttributeFilter | null>>({});
  const [coordinate, setCoordinate] = useState<[number, number] | null>(null);
  const [zoomLevel, setZoomLevel] = useState(13);
  const [scaleLabel, setScaleLabel] = useState('');
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  const [openTableLayer, setOpenTableLayer] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    objectCalcActiveRef.current = objectCalcActive;
  }, [objectCalcActive]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    // A "Поделиться" link encodes ?lat=&lon=&z= — honor it on load so opening the
    // link re-centers the map on the position it was shared from, instead of the
    // map's configured default view.
    const urlParams = new URLSearchParams(window.location.search);
    const urlLat = parseFloat(urlParams.get('lat') ?? '');
    const urlLon = parseFloat(urlParams.get('lon') ?? '');
    const urlZoom = parseFloat(urlParams.get('z') ?? '');
    const hasUrlPosition = Number.isFinite(urlLat) && Number.isFinite(urlLon);

    // MapPage unmounts and remounts fresh every time you navigate away (e.g. into
    // the object card's own route, /app/data/layers/.../objects/...) and back —
    // without this, "back to map" always lands on the map's configured default
    // view instead of wherever you actually were. sessionStorage survives that
    // round trip within the same tab but not a fresh visit, which matches what
    // "Запоминать позицию пользователя" (rememberUserPosition) is meant to do.
    const positionStorageKey = `gis:lastMapView:${mapSettings?.id ?? 'default'}`;
    let savedView: { lon: number; lat: number; zoom: number } | null = null;
    if (!hasUrlPosition && mapSettings?.behavior.rememberUserPosition !== false) {
      try {
        const raw = sessionStorage.getItem(positionStorageKey);
        if (raw) savedView = JSON.parse(raw);
      } catch {
        savedView = null;
      }
    }

    const view = new View({
      center: hasUrlPosition
        ? fromLonLat([urlLon, urlLat])
        : savedView
          ? fromLonLat([savedView.lon, savedView.lat])
          : mapCenter,
      zoom: Number.isFinite(urlZoom) ? urlZoom : (savedView?.zoom ?? initialZoom),
      minZoom: 3,
      maxZoom: 20,
    });

    const initialBasemap = pickInitialBasemap(adminBasemaps, mapSettings?.defaultBasemapName);
    const baseLayer = initialBasemap ? buildBasemapLayer(initialBasemap) : createFallbackBasemapLayer();
    const format = new GeoJSONFormat();

    const wmsLayers = layers
      .filter((layer): layer is LayerConfig & { wms: NonNullable<LayerConfig['wms']> } => Boolean(layer.wms))
      .map((layer) => {
        const source = new TileWMS({
          url: layer.wms.url,
          params: { LAYERS: layer.wms.layer, VERSION: layer.wms.version, FORMAT: layer.wms.format, TRANSPARENT: true },
        });
        const tLayer = new TileLayer({
          source,
          visible: layer.defaultVisible,
          opacity: layer.opacity ?? 1,
        });
        tileLayersRef.current[layer.id] = tLayer;
        return tLayer;
      });

    const wmtsLayers = layers
      .filter((layer) => Boolean(layer.wmts))
      .flatMap((layer) => {
        const tLayer = buildWmtsLayer(layer.wmts);
        if (!tLayer) return [];
        tLayer.setVisible(layer.defaultVisible);
        tLayer.setOpacity(layer.opacity ?? 1);
        tileLayersRef.current[layer.id] = tLayer;
        return [tLayer];
      });

    const rasterLayers = layers
      .filter((layer): layer is LayerConfig & { raster: NonNullable<LayerConfig['raster']> } => Boolean(layer.raster))
      .map((layer) => {
        const tLayer = new TileLayer({
          source: new XYZ({
            url: layer.raster.url_template,
            tileSize: layer.raster.tile_size,
            minZoom: layer.raster.min_zoom,
            maxZoom: layer.raster.max_zoom,
            crossOrigin: 'anonymous',
          }),
          visible: layer.defaultVisible,
          opacity: layer.opacity ?? 1,
        });
        tileLayersRef.current[layer.id] = tLayer;
        return tLayer;
      });

    const passesAttributeFilter = (layerId: string, feature: FeatureLike): boolean => {
      const filter = attributeFiltersRef.current[layerId];
      return !filter || matchesAttributeValue(feature.getProperties(), filter.fieldCode, filter.value);
    };

    const isMemberVisible = (feature: FeatureLike): boolean => {
      const layerId = feature.get('_clusterLayerId') as string | undefined;
      if (!layerId) return true;
      if (layerVisibilityRef.current[layerId] === false) return false;
      return passesAttributeFilter(layerId, feature);
    };

    // Every point-like layer with clustering on feeds ONE shared Cluster source
    // (built right after this loop) instead of each getting its own — a cluster
    // where two nearby points belong to different layers (e.g. Светильники и
    // Конструкции mounted on the same pole) should render as one bubble split
    // by layer color, not two separate bubbles fighting over the same pixel.
    // A single shared `groupedAway` (rather than one per layer) is enough since
    // Set membership is by feature identity, unique regardless of layer.
    const groupedAway = new Set<FeatureLike>();
    const clusterableDirectLayers: VectorLayer[] = [];
    const clusterableSource = new VectorSource();

    const vectorLayers: BaseLayer[] = layers
      .filter((layer) => !layer.wms && !layer.wmts && !layer.raster)
      .flatMap((layer): BaseLayer[] => {
        const source = new VectorSource({
          features: format.readFeatures(layer.geojson, {
            featureProjection: 'EPSG:3857',
          }),
        });
        // Always keep the raw, unclustered source here — click/box-select/spatial-query/
        // draw, and (when clustering is on) the direct layer below all look features
        // up through this map, and Cluster's synthetic "container" features
        // (feature.get('features')) reference these very same Feature objects, so a
        // selection style set via feature.setStyle() renders correctly either way.
        vectorSourcesRef.current[layer.id] = source;

        // Clustering is point-only for now — the polygon/line dual-layer approach
        // (bubble layer for actual clusters + a direct layer showing real geometry
        // for everything else) didn't hold up in practice, so those geometry types
        // go back to a single plain layer, same as clustering-off. Only revisit this
        // by resurrecting the dual-layer code below once the underlying approach is
        // actually solid — don't just re-enable it as-is.
        const isPointLike = layer.geometryType === 'point' || layer.geometryType === 'multipoint';
        if (!clusteringEnabled || !isPointLike) {
          const vLayer = new VectorLayer({
            source,
            style: ((feature: FeatureLike) =>
              passesAttributeFilter(layer.id, feature) ? resolveLayerStyle(layer, feature, false) : undefined) as unknown as StyleLike,
            visible: layer.defaultVisible,
            opacity: layer.opacity ?? 1,
          });
          vectorLayersRef.current[layer.id] = vLayer;
          return [vLayer];
        }

        // Tag each feature with its origin layer (silent — a plain bookkeeping
        // property, not something that should trigger a redraw by itself) and
        // feed the SAME instances into the shared cluster source below — a
        // feature can belong to more than one VectorSource at once (same
        // principle the old per-layer Cluster already relied on).
        for (const f of source.getFeatures()) {
          f.set('_clusterLayerId', layer.id, true);
          clusterableSource.addFeature(f);
        }

        // The direct layer draws every point NOT currently folded into a 2+
        // member bubble — which points that is gets recomputed on every shared
        // cluster regroup (zoom, pan, or data change), see below.
        const directLayer = new VectorLayer({
          source,
          visible: layer.defaultVisible,
          opacity: layer.opacity ?? 1,
          style: ((feature: FeatureLike) =>
            groupedAway.has(feature) || !passesAttributeFilter(layer.id, feature)
              ? undefined
              : resolveLayerStyle(layer, feature, false)) as unknown as StyleLike,
        });
        clusterableDirectLayers.push(directLayer);
        vectorLayersRef.current[layer.id] = directLayer;
        return [directLayer];
      });

    sharedClusterLayerRef.current = null;
    clusterableSourceRef.current = clusteringEnabled ? clusterableSource : null;
    if (clusteringEnabled && clusterableDirectLayers.length > 0) {
      const sharedCluster = new Cluster({ distance: 40, source: clusterableSource, geometryFunction: clusterGeometryFunction });
      const bubbleLayer = new VectorLayer({
        source: sharedCluster,
        style: ((feature: FeatureLike) => {
          const members = (feature.get('features') as FeatureLike[] | undefined) ?? [];
          const visibleMembers = members.filter(isMemberVisible);
          if (visibleMembers.length === 0) return undefined;
          // A "cluster" that (once hidden/filtered-out layers are excluded) has
          // only one visible member left isn't really a cluster right now —
          // show it as that member's own normal marker instead of a bubble, so
          // toggling another layer off doesn't leave a lone point with nothing
          // drawn for it (its raw dot stays hidden — groupedAway is geometric,
          // not visibility-aware — so this is the only place that draws it).
          if (visibleMembers.length === 1) {
            const m = visibleMembers[0];
            const layerId = m.get('_clusterLayerId') as string | undefined;
            const cfg = layerId ? layers.find((l) => l.id === layerId) : undefined;
            return resolveLayerStyle(cfg, m, false);
          }
          // A plain object, not `Map` — 'ol/Map' shadows the global Map
          // constructor/type in this file's scope.
          const countByLayer: Record<string, number> = {};
          for (const m of visibleMembers) {
            const layerId = (m.get('_clusterLayerId') as string | undefined) ?? '';
            countByLayer[layerId] = (countByLayer[layerId] ?? 0) + 1;
          }
          const slices = Object.entries(countByLayer).map(([layerId, count]) => ({
            color: layers.find((l) => l.id === layerId)?.color ?? '#2563eb',
            count,
          }));
          return clusterPieBubbleStyle(slices);
        }) as unknown as StyleLike,
      });
      sharedCluster.on('change', () => {
        groupedAway.clear();
        for (const clusterFeature of sharedCluster.getFeatures()) {
          const members = (clusterFeature.get('features') as FeatureLike[] | undefined) ?? [];
          if (members.length > 1) members.forEach((m) => groupedAway.add(m));
        }
        for (const dl of clusterableDirectLayers) dl.changed();
      });
      sharedClusterLayerRef.current = bubbleLayer;
      vectorLayers.push(bubbleLayer);
    }

    const measureSource = new VectorSource();
    measureSourceRef.current = measureSource;
    const measureLayer = new VectorLayer({
      source: measureSource,
      style: new Style({ stroke: new Stroke({ color: '#2563eb', width: 2, lineDash: [6, 6] }) }),
    });

    const searchHighlightSource = new VectorSource();
    searchHighlightSourceRef.current = searchHighlightSource;
    const searchHighlightLayer = new VectorLayer({
      source: searchHighlightSource,
      style: ((feature: FeatureLike) =>
        feature.get('pin') ? pinStyle() : resolveMarkerStyle({ color: '#f59e0b' }, true)) as unknown as StyleLike,
    });

    const map = new Map({
      target: containerRef.current,
      view,
      layers: [
        baseLayer,
        ...wmsLayers,
        ...wmtsLayers,
        ...rasterLayers,
        ...vectorLayers,
        measureLayer,
        searchHighlightLayer,
      ] as BaseLayer[],
      controls: [],
    });
    mapRef.current = map;

    map.on('click', (e) => {
      if (
        measureInteractionRef.current ||
        drawInteractionRef.current ||
        dragBoxRef.current ||
        spatialQueryInteractionRef.current
      ) {
        return;
      }
      searchHighlightSourceRef.current?.clear();
      setOverlapPicker(null);

      const view = map.getView();
      let found: FeatureLike | null = null;
      let clusterHandled = false;
      // Every distinct clickable feature at this pixel, across ALL layers — not
      // just the first hit forEachFeatureAtPixel reports. Without this, whichever
      // layer happens to render on top permanently wins that pixel: a layer drawn
      // underneath (e.g. Конструкции under Светильники, once both sit at the exact
      // same coordinate — see _relink_demo_parents_to_surviving_objects) becomes
      // unclickable, with no way to ever reach it.
      const hits: FeatureLike[] = [];
      let normalClusterExtent: number[] | null = null;
      map.forEachFeatureAtPixel(e.pixel, (rawFeature) => {
        let f = rawFeature as FeatureLike;
        const rawGrouped = f.get('features') as FeatureLike[] | undefined;
        // A cluster's own `features` array is the raw geometric grouping —
        // unaware of any layer's visibility or attribute filter (see
        // isMemberVisible above, which the bubble's own style already filters
        // through so the number drawn on it matches what's actually shown).
        // Without filtering here too, clicking a bubble labeled e.g. "3" could
        // still open a picker listing all 10 raw members, including ones
        // hidden by a filter and never visible on the map at all.
        const grouped = rawGrouped?.filter(isMemberVisible);
        if (grouped) {
          if (grouped.length > 1) {
            const extent = createEmpty();
            for (const cf of grouped) {
              const g = cf.getGeometry();
              if (g) extend(extent, g.getExtent());
            }
            // Not just exactly-coincident points (extent width/height == 0) —
            // members that are merely CLOSE (a few metres apart) hit the same
            // dead end: fit()'s maxZoom is capped at 19, and Cluster's own
            // `distance: 40` (px) regroups them right back together the moment
            // they're within 40px of each other again, which — at zoom 19's
            // resolution — is still true for anything under ~12m apart. A
            // cluster that fit() can never actually pull apart needs the pick-
            // one list just as much as a truly coincident one does; this was
            // computing that against a fixed 0.01m guess before, which missed
            // real (non-zero but still un-splittable) clusters and made
            // clicking them seem to randomly do nothing.
            const maxFitZoom = 19;
            const clusterPixelDistance = 40;
            const unsplittableExtent = clusterPixelDistance * view.getResolutionForZoom(maxFitZoom);
            // A dense spot (many members) reported as "sometimes the popup
            // shows, sometimes it just zooms" even after the fix above — a
            // busy area is rarely one clean cluster, it's several overlapping
            // sub-clusters at slightly different radii/centers, so which one a
            // click actually lands on (zoomable "envelope" vs. a tight
            // coincident pair inside it) varied by pixel. With many members,
            // zooming through nested regroups is bad UX anyway — go straight
            // to the pick-one list instead of gambling on which sub-cluster
            // got hit.
            const MANY_MEMBERS_THRESHOLD = 6;
            const cannotSplitByZoom = getWidth(extent) < unsplittableExtent && getHeight(extent) < unsplittableExtent;
            if (cannotSplitByZoom || grouped.length > MANY_MEMBERS_THRESHOLD) {
              hits.push(...grouped);
            } else if (!normalClusterExtent) {
              normalClusterExtent = extent;
            }
            return false;
          }
          // All raw members filtered out (hidden layer / active filter) — the
          // bubble wouldn't have rendered here at all per the style function,
          // but guard anyway rather than fall through with an undefined feature.
          if (grouped.length === 0) return false;
          f = grouped[0];
        }
        const geomType = f.getGeometry()?.getType();
        if (geomType && CLICKABLE_GEOMETRY_TYPES.has(geomType)) hits.push(f);
        return false;
      });

      const seenHitIds = new Set<string>();
      const uniqueHits = hits.filter((f) => {
        const id = f.getId();
        if (id == null) return true;
        const key = String(id);
        if (seenHitIds.has(key)) return false;
        seenHitIds.add(key);
        return true;
      });

      if (uniqueHits.length > 1) {
        const items: OverlapPickItem[] = uniqueHits.map((hf, i) => {
          const layerId = Object.keys(vectorSourcesRef.current).find((id) => vectorSourcesRef.current[id].hasFeature(hf));
          const cfg = layerId ? layers.find((l) => l.id === layerId) : undefined;
          return {
            key: String(hf.getId() ?? i),
            layerId: layerId ?? '',
            layerName: cfg?.name ?? '',
            title: pickOverlapItemTitle(getFeatureProps(hf, cfg)),
            feature: hf,
          };
        });
        setOverlapPicker({ pixel: [e.pixel[0], e.pixel[1]], items });
        clusterHandled = true;
      } else if (uniqueHits.length === 1) {
        found = uniqueHits[0];
      } else if (normalClusterExtent) {
        map.getView().fit(normalClusterExtent, { padding: [80, 80, 80, 80], maxZoom: 19, duration: 300 });
        clusterHandled = true;
      }

      if (clusterHandled) return;

      // Object calculator: intercept the click here instead of falling through to the
      // normal single-select logic below — clicking accumulates into a running tally
      // rather than opening the selection popup. See startObjectCalc.
      if (objectCalcActiveRef.current) {
        if (found) {
          const f = found as FeatureLike;
          const layerId = Object.keys(vectorSourcesRef.current).find((id) => vectorSourcesRef.current[id].hasFeature(f));
          const cfg = layerId ? layers.find((l) => l.id === layerId) : undefined;
          const rawId = f.getId();
          if (layerId && cfg && rawId != null) {
            const id = String(rawId);
            setObjectCalcItems((prev) => {
              // Locked to whatever type was clicked first — a click on a different
              // layer is simply ignored, not an error, so users can freely pan across
              // other objects while building up one tally.
              if (prev.length > 0 && prev[0].layerId !== layerId) return prev;
              const idx = prev.findIndex((it) => it.id === id);
              if (idx !== -1) {
                f.setStyle(resolveLayerStyle(cfg, f, false) as unknown as StyleLike);
                return prev.filter((_, i) => i !== idx);
              }
              f.setStyle(resolveLayerStyle(cfg, f, true) as unknown as StyleLike);
              return [
                ...prev,
                {
                  id,
                  layerId,
                  layerName: cfg.name,
                  title: pickObjectTitle(getFeatureProps(f, cfg)),
                  metrics: computeFeatureMetrics(f.getGeometry() as SimpleGeometry),
                },
              ];
            });
          }
        }
        return;
      }

      if (selectedFeatureRef.current) {
        const prevLayerId = Object.keys(vectorSourcesRef.current).find((id) =>
          vectorSourcesRef.current[id].hasFeature(selectedFeatureRef.current!),
        );
        if (prevLayerId) {
          const cfg = layers.find((l) => l.id === prevLayerId);
          if (cfg) selectedFeatureRef.current.setStyle(resolveLayerStyle(cfg, selectedFeatureRef.current, false) as unknown as StyleLike);
        }
      }

      if (found) {
        const layerId = Object.keys(vectorSourcesRef.current).find((id) =>
          vectorSourcesRef.current[id].hasFeature(found!),
        );
        const cfg = layerId ? layers.find((l) => l.id === layerId) : undefined;
        (found as FeatureLike).setStyle(resolveLayerStyle(cfg, found as FeatureLike, true) as unknown as StyleLike);
        selectedFeatureRef.current = found;
        setSelectedFeature(getFeatureProps(found as FeatureLike, cfg));
        setSelectedFeatureLayerId(layerId ?? null);
        const foundId = (found as FeatureLike).getId();
        setSelectedFeatureId(foundId != null ? String(foundId) : null);
        setSelectedFeaturePhotoFileId(foundId != null ? (cfg?.photoByFeatureId?.[String(foundId)] ?? null) : null);
        setSelectedFeatureMetrics(computeFeatureMetrics((found as FeatureLike).getGeometry() as SimpleGeometry));
      } else {
        selectedFeatureRef.current = null;
        setSelectedFeature(null);
        setSelectedFeatureId(null);
        setSelectedFeatureLayerId(null);
        setSelectedFeaturePhotoFileId(null);
        setSelectedFeatureMetrics(null);
      }
    });

    map.on('pointermove', (e) => {
      const [lon, lat] = toLonLat(e.coordinate);
      setCoordinate([lon, lat]);
    });

    const updateScale = () => {
      const [, lat] = toLonLat(view.getCenter() ?? mapCenter);
      setScaleLabel(formatScaleLabel(view.getResolution() ?? 1, lat));
    };

    view.on('change:resolution', () => {
      setZoomLevel(Math.round(view.getZoom() ?? 13));
      updateScale();
    });
    map.on('moveend', updateScale);
    updateScale();

    const saveViewPosition = () => {
      if (mapSettings?.behavior.rememberUserPosition === false) return;
      const [lon, lat] = toLonLat(view.getCenter() ?? mapCenter);
      const zoom = view.getZoom() ?? initialZoom;
      try {
        sessionStorage.setItem(positionStorageKey, JSON.stringify({ lon, lat, zoom }));
      } catch {
        // sessionStorage недоступен (приватный режим и т.п.) — просто не запоминаем
      }
    };
    map.on('moveend', saveViewPosition);

    // #map-print-target's @media print rule (index.css) switches it to
    // position:fixed/100vw/100vh for printing — but OL only notices a container
    // resize via its own (async) ResizeObserver, which doesn't fire in time for
    // window.print()'s synchronous snapshot. Without this, the canvas still has
    // its old, on-screen-sized content baked in, so the print output is the old
    // view stretched/cropped into the new page instead of a properly redrawn
    // full view. beforeprint fires synchronously with the print-CSS already
    // applied, so updateSize() here picks up the correct fullscreen dimensions
    // before the browser captures the page. afterprint restores it back.
    const handleBeforePrint = () => map.updateSize();
    const handleAfterPrint = () => map.updateSize();
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    setReady(true);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
      map.setTarget(undefined);
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Viewport-scoped loading for layers too large to fetch in one shot (see
  // realLayers.ts's LAZY_FEATURE_THRESHOLD) — their VectorSource starts empty (built in
  // the mount effect above like any other layer) and gets filled in here, scoped to
  // what's actually on screen, refreshed on every pan/zoom. Swapping features into the
  // SAME VectorSource object that vectorSourcesRef already holds means click handling,
  // clustering, box-select, spatial queries and draw keep working unchanged — they all
  // read through that one shared source regardless of how it got populated.
  //
  // Trade-off worth knowing: spatial tools (box-select, spatial query, draw-snap) on a
  // lazy layer only ever see what's currently loaded for the visible viewport, not the
  // whole layer — same limitation any viewport-loaded web map has.
  useEffect(() => {
    if (!ready) return;
    const map = mapRef.current;
    if (!map) return;
    const view = map.getView();
    const format = new GeoJSONFormat();
    const lazyLayers = layers.filter((l) => l.lazy);
    if (lazyLayers.length === 0) return;

    let cancelled = false;

    const refresh = () => {
      const size = map.getSize();
      if (!size) return;
      const extent3857 = view.calculateExtent(size);
      const [minLon, minLat] = toLonLat([extent3857[0], extent3857[1]]);
      const [maxLon, maxLat] = toLonLat([extent3857[2], extent3857[3]]);

      for (const layer of lazyLayers) {
        if (layerVisibility[layer.id] === false) continue;
        const source = vectorSourcesRef.current[layer.id];
        if (!source) continue;

        const isPointLike = layer.geometryType === 'point' || layer.geometryType === 'multipoint';
        const clusterableSource = isPointLike ? clusterableSourceRef.current : null;

        layersApi
          .getFeatures(layer.id, [minLon, minLat, maxLon, maxLat])
          .then((fc) => {
            if (cancelled) return;
            if (!layer.photoByFeatureId) layer.photoByFeatureId = {};
            for (const f of fc.features) layer.photoByFeatureId[String(f.id)] = f.photo_file_id ?? null;
            const parsed = format.readFeatures(fc, { featureProjection: 'EPSG:3857' });
            // The shared cross-layer cluster source (see the mount effect above)
            // holds these exact feature instances too — swap them out there in
            // lockstep, or this layer's points stop clustering with any other
            // layer's the moment the viewport moves.
            if (clusterableSource) {
              for (const old of source.getFeatures()) clusterableSource.removeFeature(old);
            }
            source.clear(true);
            source.addFeatures(parsed);
            if (clusterableSource) {
              for (const f of parsed) {
                f.set('_clusterLayerId', layer.id, true);
                clusterableSource.addFeature(f);
              }
            }
          })
          .catch(() => {
            // сеть подвела — оставляем то, что уже отрисовано, до следующего moveend
          });
      }
    };

    refresh();
    const key = map.on('moveend', refresh);
    return () => {
      cancelled = true;
      unByKey(key);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, layerVisibility]);

  const setLayerVisible = (id: string, visible: boolean) => {
    setLayerVisibilityState((prev) => ({ ...prev, [id]: visible }));
    layerVisibilityRef.current = { ...layerVisibilityRef.current, [id]: visible };
    vectorLayersRef.current[id]?.setVisible(visible);
    tileLayersRef.current[id]?.setVisible(visible);
    sharedClusterLayerRef.current?.changed();
  };

  /** Filters (or clears, with filter=null) which objects of one layer render —
   * see AttributeFilter. `.changed()` forces every feature's style function to
   * re-run immediately; without it the hide/show only takes effect on the next
   * unrelated redraw (pan/zoom). */
  const setAttributeFilter = (layerId: string, filter: AttributeFilter | null) => {
    attributeFiltersRef.current = { ...attributeFiltersRef.current, [layerId]: filter };
    setAttributeFilters(attributeFiltersRef.current);
    vectorLayersRef.current[layerId]?.changed();
    sharedClusterLayerRef.current?.changed();
  };

  const setLayerOpacity = (id: string, opacity: number) => {
    setLayerOpacityState((prev) => ({ ...prev, [id]: opacity }));
    vectorLayersRef.current[id]?.setOpacity(opacity);
    tileLayersRef.current[id]?.setOpacity(opacity);
  };

  const clearSelection = () => {
    if (selectedFeatureRef.current) {
      const prevLayerId = Object.keys(vectorSourcesRef.current).find((id) =>
        vectorSourcesRef.current[id].hasFeature(selectedFeatureRef.current!),
      );
      if (prevLayerId) {
        const cfg = layers.find((l) => l.id === prevLayerId);
        if (cfg) selectedFeatureRef.current.setStyle(resolveLayerStyle(cfg, selectedFeatureRef.current, false) as unknown as StyleLike);
      }
    }
    selectedFeatureRef.current = null;
    setSelectedFeature(null);
    setSelectedFeatureId(null);
    setSelectedFeatureLayerId(null);
    setSelectedFeaturePhotoFileId(null);
    setSelectedFeatureMetrics(null);
    searchHighlightSourceRef.current?.clear();
    setSelectionResults([]);
    setSelectionIndexState(0);
  };

  const deleteSelectedFeature = async () => {
    const feature = selectedFeatureRef.current;
    const layerId = selectedFeatureLayerId;
    if (!feature || !layerId) return;
    const id = feature.getId();
    if (id == null) return;
    await layersApi.deleteFeature(layerId, String(id));
    removeFeatureFromMap(layerId, String(id));
    setSelectionResults((prev) => prev.filter((r) => r.id !== String(id)));
    clearSelection();
  };

  const flyToSearchHit = (geometry: GeoJsonGeometryLike, properties: DemoFeatureProps, opts?: { pin?: boolean }) => {
    const map = mapRef.current;
    const source = searchHighlightSourceRef.current;
    if (!map || !source) return;

    clearSelection();
    const format = new GeoJSONFormat();
    const feature = format.readFeature(
      { type: 'Feature', geometry, properties: {} },
      { featureProjection: 'EPSG:3857' },
    ) as FeatureLike;
    if (opts?.pin) feature.set('pin', true);
    source.addFeature(feature);
    setSelectedFeature(properties);
    setSelectedFeatureLayerId(null);
    setSelectedFeaturePhotoFileId(null);

    const extent = feature.getGeometry()?.getExtent();
    if (extent) map.getView().fit(extent, { padding: [80, 80, 80, 80], maxZoom: 17, duration: 400 });
  };

  const flyToCoordinates = (lon: number, lat: number) => {
    flyToSearchHit(
      { type: 'Point', coordinates: [lon, lat] },
      { name: `${lat.toFixed(5)}, ${lon.toFixed(5)}` },
      { pin: true },
    );
  };

  const getViewState = () => {
    const view = mapRef.current?.getView();
    const center = view?.getCenter();
    const zoom = view?.getZoom();
    if (!center || zoom == null) return null;
    const [lon, lat] = toLonLat(center);
    return { lon, lat, zoom };
  };

  const openLayerTable = (id: string, name: string) => setOpenTableLayer({ id, name });
  const closeLayerTable = () => setOpenTableLayer(null);

  const setBasemap = (id: string) => {
    const map = mapRef.current;
    const found = adminBasemaps.find((b) => b.id === id);
    if (!map || !found) return;
    const mapLayers = map.getLayers();
    mapLayers.setAt(0, buildBasemapLayer(found));
    setActiveBasemapId(id);
  };

  const zoomIn = () => mapRef.current?.getView().animate({ zoom: (mapRef.current.getView().getZoom() ?? 13) + 1, duration: 200 });
  const zoomOut = () => mapRef.current?.getView().animate({ zoom: (mapRef.current.getView().getZoom() ?? 13) - 1, duration: 200 });

  const fitToLayers = () => {
    const map = mapRef.current;
    if (!map) return;
    map.getView().animate({ center: mapCenter, zoom: initialZoom, duration: 300 });
  };

  const fitToLayerExtent = (layerId: string) => {
    const map = mapRef.current;
    const source = vectorSourcesRef.current[layerId];
    if (!map || !source) return;
    const extent = source.getExtent();
    if (!extent || isExtentEmpty(extent)) return;
    map.getView().fit(extent, { padding: [80, 80, 80, 80], duration: 400, maxZoom: 18 });
  };

  const locate = () => {
    if (!navigator.geolocation) {
      setLocateError('Геолокация не поддерживается этим браузером');
      return;
    }
    setLocating(true);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { longitude, latitude } = pos.coords;
        flyToSearchHit({ type: 'Point', coordinates: [longitude, latitude] }, { name: 'Моё местоположение' });
        mapRef.current?.getView().animate({ zoom: 15, duration: 400 });
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        // These are three genuinely different failures, not one generic "no
        // location" — and the browser-permission case is the ONE this used to
        // mislabel: PERMISSION_DENIED also fires when the site permission is
        // granted but Windows itself has location blocked for the browser
        // (Settings → Privacy & security → Location — a separate, OS-level
        // toggle Chromium checks in addition to the per-site permission), so
        // "granted it in the browser and it still fails" is exactly this case.
        let message: string;
        if (err.code === err.PERMISSION_DENIED) {
          message =
            'Геолокация недоступна, хотя разрешение в браузере есть — скорее всего, её блокирует Windows. ' +
            'Откройте Параметры Windows → Конфиденциальность и защита → Расположение, включите службу геолокации ' +
            'и разрешите доступ для вашего браузера, затем обновите страницу.';
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          message = 'Не удалось определить координаты — устройство не смогло получить сигнал местоположения';
        } else if (err.code === err.TIMEOUT) {
          message = 'Определение местоположения заняло слишком много времени — попробуйте ещё раз';
        } else {
          message = 'Не удалось определить местоположение';
        }
        setLocateError(message);
      },
      { timeout: 15000, enableHighAccuracy: false, maximumAge: 60000 },
    );
  };

  const stopMeasure = () => {
    const map = mapRef.current;
    if (map) {
      if (measureInteractionRef.current) map.removeInteraction(measureInteractionRef.current);
      if (measureTooltipRef.current) map.removeOverlay(measureTooltipRef.current);
    }
    measureInteractionRef.current = null;
    measureTooltipRef.current = null;
    measureSourceRef.current?.clear();
    setMeasureActive(false);
    setMeasureMode(null);
    setMeasureShape(null);
  };

  const stopBoxSelect = () => {
    const map = mapRef.current;
    if (map && dragBoxRef.current) map.removeInteraction(dragBoxRef.current);
    dragBoxRef.current = null;
    activeBoxExtentRef.current = null;
    setBoxSelectActive(false);
  };

  const stopSpatialQuery = () => {
    const map = mapRef.current;
    if (map && spatialQueryInteractionRef.current) map.removeInteraction(spatialQueryInteractionRef.current);
    spatialQueryInteractionRef.current = null;
    measureSourceRef.current?.clear();
    setSpatialQueryActive(false);
  };

  /** Same shared draw-overlay source as spatialQuery, but this one doesn't
   * select map features itself — it just hands the finished polygon (as
   * GeoJSON) to whoever asked for it (AiPanel), which sends it to ai-service.
   * The drawn shape stays on the map (not cleared) so the user can see what
   * area the analysis is about while results come back. */
  const stopAiAreaSelect = () => {
    const map = mapRef.current;
    if (map && aiAreaInteractionRef.current) map.removeInteraction(aiAreaInteractionRef.current);
    aiAreaInteractionRef.current = null;
    setAiAreaSelectActive(false);
  };

  const clearAiAreaGeometry = () => {
    measureSourceRef.current?.clear();
    setAiAreaGeometry(null);
  };

  const stopDraw = () => {
    stopAddObject();
    setDrawActive(false);
    setDrawLayerId(null);
  };

  const UNIT_CYCLES: Record<MeasureMode, { label: string; factor: number }[]> = {
    distance: [
      { label: 'м', factor: 1 },
      { label: 'км', factor: 0.001 },
    ],
    perimeter: [
      { label: 'м', factor: 1 },
      { label: 'км', factor: 0.001 },
    ],
    area: [
      { label: 'м²', factor: 1 },
      { label: 'сот.', factor: 0.01 },
      { label: 'га', factor: 0.0001 },
    ],
  };

  const formatMeasureValue = (value: number, mode: MeasureMode, unitIndex: number): string => {
    const units = UNIT_CYCLES[mode];
    const unit = units[unitIndex % units.length];
    const converted = value * unit.factor;
    const formatted = converted >= 100 ? converted.toFixed(0) : converted.toFixed(2);
    return `${formatted} ${unit.label}`;
  };

  /** Unified measurement tool — distance/area/perimeter over polygon/circle/rectangle
   * shapes. The result tooltip stays anchored to the drawn shape after drawend (it's
   * not auto-cleared) and shows one button per applicable unit. */
  const startMeasure = (mode: MeasureMode, shape: MeasureShape = 'polygon') => {
    const map = mapRef.current;
    if (!map) return;
    stopMeasure();
    stopDraw();
    stopBoxSelect();
    stopSpatialQuery();
    stopObjectCalc();
    setMeasureMode(mode);
    setMeasureShape(mode === 'distance' ? null : shape);

    const olType: 'LineString' | 'Polygon' | 'Circle' =
      mode === 'distance' ? 'LineString' : shape === 'circle' ? 'Circle' : 'Polygon';

    const draw = new Draw({
      source: measureSourceRef.current!,
      type: olType,
      geometryFunction: shape === 'rectangle' && mode !== 'distance' ? createBox() : undefined,
    });

    // A value readout plus one visible button per applicable unit (not a single
    // click-to-cycle target) — the whole point is that every unit option is
    // right there to see and click, nobody has to guess that clicking cycles
    // through hidden options.
    const tooltipEl = document.createElement('div');
    tooltipEl.className =
      'flex select-none items-center gap-1.5 rounded-md bg-bg-navigation px-2 py-1 text-[11px] font-medium text-text-inverse shadow';
    const valueEl = document.createElement('span');
    tooltipEl.appendChild(valueEl);
    const unitsEl = document.createElement('div');
    unitsEl.className = 'flex items-center gap-0.5';
    tooltipEl.appendChild(unitsEl);

    const units = UNIT_CYCLES[mode];
    let unitIndex = 0;
    const unitButtons = units.map((unit, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = unit.label;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        unitIndex = i;
        render();
      });
      unitsEl.appendChild(btn);
      return btn;
    });

    const tooltip = new Overlay({ element: tooltipEl, offset: [12, -8], positioning: 'bottom-left' });
    map.addOverlay(tooltip);
    measureTooltipRef.current = tooltip;

    let lastGeom: SimpleGeometry | null = null;

    const render = () => {
      if (!lastGeom) return;
      let primary = 0;
      let extra = '';
      let posCoord: number[] | undefined;

      if (lastGeom instanceof CircleGeom) {
        const center = lastGeom.getCenter();
        const r = lastGeom.getRadius();
        posCoord = [center[0] + r, center[1]];
        if (mode === 'area') {
          primary = Math.PI * r * r;
          const radiusLabel = r > 1000 ? `${(r / 1000).toFixed(2)} км` : `${r.toFixed(0)} м`;
          extra = ` · радиус ${radiusLabel}`;
        } else {
          primary = 2 * Math.PI * r;
        }
      } else if (lastGeom.getType() === 'LineString') {
        primary = getLength(lastGeom);
        posCoord = (lastGeom as unknown as { getLastCoordinate: () => number[] }).getLastCoordinate();
      } else {
        const rings = (lastGeom as unknown as { getCoordinates: () => number[][][] }).getCoordinates();
        const ring = rings[0] ?? [];
        posCoord = ring[ring.length - 2] ?? ring[ring.length - 1];
        primary = mode === 'area' ? getArea(lastGeom) : getLength(lastGeom);
      }

      valueEl.textContent = `${formatMeasureValue(primary, mode, unitIndex)}${extra}`;
      unitButtons.forEach((btn, i) => {
        btn.className =
          i === unitIndex
            ? 'rounded bg-white/25 px-1 py-0.5 text-[10px] font-semibold text-text-inverse'
            : 'rounded px-1 py-0.5 text-[10px] font-medium text-text-inverse/60 hover:text-text-inverse';
      });
      if (posCoord) tooltip.setPosition(posCoord);
    };

    draw.on('drawstart', (evt) => {
      const geometry = evt.feature.getGeometry() as SimpleGeometry;
      lastGeom = geometry;
      geometry.on('change', render);
      render();
    });

    draw.on('drawend', () => {
      map.removeInteraction(draw);
      measureInteractionRef.current = null;
      setMeasureActive(false);
      // Deliberately don't clear the drawn shape/tooltip — it's the result the user
      // asked for. It gets cleared on the next startMeasure()/stopMeasure() call.
    });

    map.addInteraction(draw);
    measureInteractionRef.current = draw;
    setMeasureActive(true);
  };

  const applySelectionResults = (results: SelectionResult[]) => {
    setSelectionResults(results);
    setSelectionIndexState(0);
    if (results.length > 0) {
      const first = results[0];
      setSelectedFeature(first.properties);
      setSelectedFeatureId(first.id);
      setSelectedFeatureLayerId(first.layerId);
      setSelectedFeaturePhotoFileId(first.photoFileId);
      setSelectedFeatureMetrics(null);
    } else {
      setSelectedFeature(null);
      setSelectedFeatureId(null);
      setSelectedFeatureLayerId(null);
      setSelectedFeaturePhotoFileId(null);
    }
  };

  const setSelectionIndex = (i: number) => {
    const clamped = Math.max(0, Math.min(i, selectionResults.length - 1));
    setSelectionIndexState(clamped);
    const item = selectionResults[clamped];
    if (item) {
      setSelectedFeature(item.properties);
      setSelectedFeatureId(item.id);
      setSelectedFeatureLayerId(item.layerId);
      setSelectedFeaturePhotoFileId(item.photoFileId);
    }
  };

  const clearSelectionResults = () => {
    setSelectionResults([]);
    setSelectionIndexState(0);
    activeBoxExtentRef.current = null;
  };

  /** Box-select in edit mode (scope='active') deletes by AREA, not by the id list
   * in selectionResults — that list is only a preview, capped the same way the
   * lazy-loading bbox query is (_MAX_FEATURES_RESPONSE server-side), so an area
   * with more objects than the cap would silently leave the rest behind. The
   * server's delete-by-bbox filters directly in its UPDATE, so there's no cap.
   * Any other kind of multi-select (the 'all'-scope toolbar tool) still goes
   * through one bulk request per layer instead of one DELETE per object. */
  const deleteSelectionResults = async (): Promise<{ deleted: number; failed: number }> => {
    if (drawLayerId && activeBoxExtentRef.current) {
      const { mapExtent, bbox } = activeBoxExtentRef.current;
      const layerId = drawLayerId;
      activeBoxExtentRef.current = null;
      try {
        const { deleted } = await layersApi.deleteFeaturesByBbox(layerId, bbox);
        const source = vectorSourcesRef.current[layerId];
        if (source) {
          const toRemove: FeatureLike[] = [];
          source.forEachFeatureIntersectingExtent(mapExtent, (f) => toRemove.push(f as FeatureLike));
          for (const f of toRemove) source.removeFeature(f);
        }
        setSelectionResults([]);
        clearSelection();
        return { deleted, failed: 0 };
      } catch {
        return { deleted: 0, failed: 1 };
      }
    }

    const targets = drawLayerId ? selectionResults.filter((r) => r.layerId === drawLayerId) : selectionResults;
    const idsByLayer = new globalThis.Map<string, string[]>();
    for (const r of targets) idsByLayer.set(r.layerId, [...(idsByLayer.get(r.layerId) ?? []), r.id]);

    const deletedIds = new Set<string>();
    let failed = 0;
    await Promise.all(
      Array.from(idsByLayer.entries()).map(async ([layerId, ids]) => {
        try {
          await layersApi.bulkDeleteFeatures(layerId, ids);
          for (const id of ids) {
            removeFeatureFromMap(layerId, id);
            deletedIds.add(id);
          }
        } catch {
          failed += ids.length;
        }
      }),
    );
    setSelectionResults((prev) => prev.filter((r) => !deletedIds.has(r.id)));
    clearSelection();
    return { deleted: deletedIds.size, failed };
  };

  /** Rectangle drag-select — по умолчанию across every visible layer, or scoped to
   * the layer currently being edited (drawLayerId) when scope='active'. */
  const startBoxSelect = (scope: 'all' | 'active') => {
    const map = mapRef.current;
    if (!map) return;
    stopBoxSelect();
    stopMeasure();
    stopSpatialQuery();
    stopObjectCalc();
    stopAddObject();

    const dragBox = new DragBox({});
    dragBox.on('boxend', () => {
      const extent = dragBox.getGeometry().getExtent();

      if (scope === 'active' && drawLayerId) {
        // A lazy layer (see realLayers.ts) only ever holds whatever features the
        // current viewport already fetched — scanning that local source silently
        // misses anything inside the box that just isn't loaded client-side yet
        // (e.g. after zooming/panning away). Asking the backend for this exact
        // bbox is the only way box-select-and-delete is complete regardless of
        // what happens to be rendered right now.
        const [minLon, minLat] = toLonLat([extent[0], extent[1]]) as [number, number];
        const [maxLon, maxLat] = toLonLat([extent[2], extent[3]]) as [number, number];
        const bbox: [number, number, number, number] = [minLon, minLat, maxLon, maxLat];
        activeBoxExtentRef.current = { mapExtent: extent, bbox };
        const layerId = drawLayerId;
        const cfg = layers.find((l) => l.id === layerId);
        layersApi
          .getFeatures(layerId, bbox)
          .then((fc) => {
            applySelectionResults(
              fc.features.map((f) => ({
                id: f.id,
                layerId,
                layerName: cfg?.name ?? '',
                properties: f.properties as DemoFeatureProps,
                photoFileId: f.photo_file_id ?? null,
              })),
            );
          })
          .catch(() => applySelectionResults([]));
        return;
      }

      const results: SelectionResult[] = [];
      for (const [layerId, source] of Object.entries(vectorSourcesRef.current)) {
        const cfg = layers.find((l) => l.id === layerId);
        if (!source || !cfg) continue;
        source.forEachFeatureIntersectingExtent(extent, (f) => {
          const id = f.getId();
          if (id == null) return;
          results.push({
            id: String(id),
            layerId,
            layerName: cfg.name,
            properties: getFeatureProps(f as FeatureLike, cfg),
            photoFileId: cfg.photoByFeatureId?.[String(id)] ?? null,
          });
        });
      }
      applySelectionResults(results);
    });

    map.addInteraction(dragBox);
    dragBoxRef.current = dragBox;
    setBoxSelectActive(true);
  };

  /** Draws a query polygon and selects everything under it — precise containment
   * test for points, extent-intersection approximation for lines/polygons (no
   * geometry-clipping library in this project yet). */
  const startSpatialQuery = () => {
    const map = mapRef.current;
    if (!map) return;
    stopSpatialQuery();
    stopMeasure();
    stopBoxSelect();
    stopDraw();
    stopObjectCalc();

    const draw = new Draw({ source: measureSourceRef.current!, type: 'Polygon' });
    draw.on('drawend', (evt) => {
      map.removeInteraction(draw);
      spatialQueryInteractionRef.current = null;
      setSpatialQueryActive(false);

      const queryGeom = evt.feature.getGeometry() as SimpleGeometry & {
        getExtent: () => number[];
        intersectsCoordinate: (c: number[]) => boolean;
      };
      const extent = queryGeom.getExtent();
      const results: SelectionResult[] = [];
      for (const [layerId, source] of Object.entries(vectorSourcesRef.current)) {
        const cfg = layers.find((l) => l.id === layerId);
        if (!cfg) continue;
        source.forEachFeatureIntersectingExtent(extent, (f) => {
          const geom = f.getGeometry();
          const isPoint = geom?.getType() === 'Point';
          const matches = isPoint
            ? queryGeom.intersectsCoordinate((geom as unknown as { getCoordinates: () => number[] }).getCoordinates())
            : true;
          if (!matches) return;
          const id = f.getId();
          if (id == null) return;
          results.push({
            id: String(id),
            layerId,
            layerName: cfg.name,
            properties: getFeatureProps(f as FeatureLike, cfg),
            photoFileId: cfg.photoByFeatureId?.[String(id)] ?? null,
          });
        });
      }
      applySelectionResults(results);
    });

    map.addInteraction(draw);
    spatialQueryInteractionRef.current = draw;
    setSpatialQueryActive(true);
  };

  /** Draws a polygon and hands it (as GeoJSON, EPSG:4326) to whoever asked —
   * AiPanel sends it to ai-service's /analyze. Unlike startSpatialQuery this
   * doesn't touch selectionResults at all, it's a separate concern. */
  const startAiAreaSelect = () => {
    const map = mapRef.current;
    if (!map) return;
    stopAiAreaSelect();
    stopSpatialQuery();
    stopMeasure();
    stopBoxSelect();
    stopDraw();
    stopObjectCalc();
    setAiAreaGeometry(null);
    measureSourceRef.current?.clear();

    const draw = new Draw({ source: measureSourceRef.current!, type: 'Polygon' });
    draw.on('drawend', (evt) => {
      map.removeInteraction(draw);
      aiAreaInteractionRef.current = null;
      setAiAreaSelectActive(false);

      const format = new GeoJSONFormat();
      const geometry = format.writeGeometryObject(evt.feature.getGeometry()!, {
        featureProjection: 'EPSG:3857',
        dataProjection: 'EPSG:4326',
      }) as { type: string; coordinates: unknown };
      setAiAreaGeometry(geometry);
    });

    map.addInteraction(draw);
    aiAreaInteractionRef.current = draw;
    setAiAreaSelectActive(true);
  };

  const toggleObjectCalculator = () => setObjectCalculatorEnabled((v) => !v);

  /** Starts the geometry-drawing tool targeting one specific layer — the layer's
   * geometry_type fixes the OL Draw shape (a point layer only draws points, etc.).
   * Each finished shape is posted to the backend immediately; on success it's
   * tagged with the real id/properties, on failure it's removed from the map so
   * the map never shows an object that doesn't exist in the database. */
  /** Opens the editing session/banner for a layer — deliberately does NOT arm
   * the click-to-add interaction (see startAddObject): entering "Редактирование
   * объектов" should default to plain click-to-select, same as normal browsing,
   * not silently turn every click into a new object underneath whatever's
   * already there. */
  const startDraw = (layerId: string) => {
    const map = mapRef.current;
    if (!map) return;
    const cfg = layers.find((l) => l.id === layerId);
    if (!cfg?.geometryType || !vectorSourcesRef.current[layerId]) return;

    stopDraw();
    stopMeasure();
    stopBoxSelect();
    stopSpatialQuery();
    stopObjectCalc();
    setDrawError(null);
    setDrawActive(true);
    setDrawLayerId(layerId);
  };

  const stopAddObject = () => {
    const map = mapRef.current;
    if (map) {
      if (drawInteractionRef.current) map.removeInteraction(drawInteractionRef.current);
      if (snapInteractionRef.current) map.removeInteraction(snapInteractionRef.current);
    }
    drawInteractionRef.current = null;
    snapInteractionRef.current = null;
    setAddObjectActive(false);
  };

  /** The explicit "click to place a new object" step within an already-open
   * editing session (see startDraw) — targets whichever layer that session is
   * on. Mutually exclusive with box-select (see startBoxSelect): only one of
   * the two pointer interactions is ever armed at a time, so a click can't be
   * read as both "add" and "select for bulk action" at once. */
  const startAddObject = () => {
    const map = mapRef.current;
    if (!map || !drawLayerId) return;
    const cfg = layers.find((l) => l.id === drawLayerId);
    const olType = cfg?.geometryType ? OL_DRAW_TYPE[cfg.geometryType] : undefined;
    const source = vectorSourcesRef.current[drawLayerId];
    if (!olType || !source) return;

    stopAddObject();
    stopBoxSelect();
    const layerId = drawLayerId;

    const draw = new Draw({ source, type: olType });
    draw.on('drawend', (evt) => {
      const format = new GeoJSONFormat();
      const geometry = format.writeGeometryObject(evt.feature.getGeometry()!, {
        featureProjection: 'EPSG:3857',
        dataProjection: 'EPSG:4326',
      }) as { type: string; coordinates: unknown };

      layersApi
        .createFeature(layerId, geometry)
        .then((created) => {
          evt.feature.setId(created.id);
          evt.feature.setProperties(created.properties, true);
        })
        .catch((err) => {
          source.removeFeature(evt.feature);
          setDrawError(err instanceof ApiError ? err.message : 'Не удалось сохранить объект.');
        });
    });

    map.addInteraction(draw);
    drawInteractionRef.current = draw;

    // Snaps new vertices to the ones already placed on the SAME in-progress shape
    // (draw's own sketch overlay) — makes it easy to click precisely back onto the
    // start point to close a line/polygon instead of needing a double-click.
    // Must be added after the Draw interaction to take effect (OL requirement).
    const snap = new Snap({ source: draw.getOverlay().getSource() as VectorSource });
    map.addInteraction(snap);
    snapInteractionRef.current = snap;

    setAddObjectActive(true);
  };

  /** Appends a vertex at a typed coordinate instead of a map click — for a
   * point layer that's the whole object, so the shape finishes immediately;
   * for a line/polygon it's just one more vertex, exactly like clicking would
   * add, and the user still finishes the shape normally (double-click/Enter/
   * closing the ring). Requires startAddObject to already be running. */
  const addObjectAtCoordinate = (lon: number, lat: number) => {
    const draw = drawInteractionRef.current;
    const cfg = drawLayerId ? layers.find((l) => l.id === drawLayerId) : undefined;
    if (!draw || !cfg) return;
    draw.appendCoordinates([fromLonLat([lon, lat])]);
    if (cfg.geometryType === 'point') draw.finishDrawing();
  };

  /** Restores each accumulated item's feature to its normal (non-highlighted) style —
   * called whenever the running selection is cleared or the tool stops. A feature that
   * scrolled out of a lazy layer's currently-loaded viewport (see realLayers.ts) simply
   * won't be found here and is skipped — its highlight was already gone with it. */
  const resetObjectCalcStyles = (items: ObjectCalcSelection[]) => {
    for (const item of items) {
      const feature = vectorSourcesRef.current[item.layerId]?.getFeatureById(item.id);
      if (!feature) continue;
      const cfg = layers.find((l) => l.id === item.layerId);
      feature.setStyle(resolveLayerStyle(cfg, feature, false) as unknown as StyleLike);
    }
  };

  const startObjectCalc = () => {
    const map = mapRef.current;
    if (!map) return;
    stopMeasure();
    stopBoxSelect();
    stopSpatialQuery();
    stopAiAreaSelect();
    stopDraw();
    clearSelection();
    clearSelectionResults();
    setObjectCalcItems((prev) => {
      resetObjectCalcStyles(prev);
      return [];
    });
    setObjectCalcActive(true);
  };

  const stopObjectCalc = () => {
    setObjectCalcItems((prev) => {
      resetObjectCalcStyles(prev);
      return [];
    });
    setObjectCalcActive(false);
  };

  const clearObjectCalc = () => {
    setObjectCalcItems((prev) => {
      resetObjectCalcStyles(prev);
      return [];
    });
  };

  /** Applies the normal single-feature selection (same effect as clicking it
   * directly) for whichever item the user picked from the overlap list — see
   * the degenerate-extent branch in the map click handler above. */
  const selectOverlapItem = (item: OverlapPickItem) => {
    if (selectedFeatureRef.current) {
      const prevLayerId = Object.keys(vectorSourcesRef.current).find((id) =>
        vectorSourcesRef.current[id].hasFeature(selectedFeatureRef.current!),
      );
      const prevCfg = prevLayerId ? layers.find((l) => l.id === prevLayerId) : undefined;
      if (prevCfg) selectedFeatureRef.current.setStyle(resolveLayerStyle(prevCfg, selectedFeatureRef.current, false) as unknown as StyleLike);
    }
    const cfg = layers.find((l) => l.id === item.layerId);
    item.feature.setStyle(resolveLayerStyle(cfg, item.feature, true) as unknown as StyleLike);
    selectedFeatureRef.current = item.feature;
    setSelectedFeature(getFeatureProps(item.feature, cfg));
    setSelectedFeatureLayerId(item.layerId || null);
    const fId = item.feature.getId();
    setSelectedFeatureId(fId != null ? String(fId) : null);
    setSelectedFeaturePhotoFileId(fId != null ? (cfg?.photoByFeatureId?.[String(fId)] ?? null) : null);
    setSelectedFeatureMetrics(computeFeatureMetrics(item.feature.getGeometry() as SimpleGeometry));
    setOverlapPicker(null);
  };

  const closeOverlapPicker = () => setOverlapPicker(null);

  const objectCalcTotals = useMemo(() => {
    let lengthM = 0;
    let areaM2 = 0;
    let hasLength = false;
    let hasArea = false;
    for (const item of objectCalcItems) {
      if (item.metrics?.kind === 'line') {
        lengthM += item.metrics.lengthM;
        hasLength = true;
      } else if (item.metrics?.kind === 'polygon') {
        areaM2 += item.metrics.areaM2;
        hasArea = true;
      }
    }
    return { count: objectCalcItems.length, lengthM: hasLength ? lengthM : null, areaM2: hasArea ? areaM2 : null };
  }, [objectCalcItems]);

  const removeFeatureFromMap = (layerId: string, featureId: string) => {
    const source = vectorSourcesRef.current[layerId];
    const feature = source?.getFeatureById(featureId);
    if (feature) source?.removeFeature(feature);
  };

  useEffect(() => {
    if (!drawActive) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stopDraw();
      else if (e.key === 'Enter') drawInteractionRef.current?.finishDrawing();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawActive]);

  const value = useMemo<MapContextValue>(
    () => ({
      containerRef,
      ready,
      layers,
      layerVisibility,
      layerOpacity,
      setLayerVisible,
      setLayerOpacity,
      selectedFeature,
      selectedFeatureId,
      selectedFeatureLayerId,
      selectedFeaturePhotoFileId,
      selectedFeatureMetrics,
      clearSelection,
      deleteSelectedFeature,
      activeBasemapId,
      setBasemap,
      zoomIn,
      zoomOut,
      locate,
      locating,
      locateError,
      clearLocateError: () => setLocateError(null),
      measureActive,
      measureMode,
      measureShape,
      startMeasure,
      stopMeasure,
      boxSelectActive,
      startBoxSelect,
      stopBoxSelect,
      spatialQueryActive,
      startSpatialQuery,
      stopSpatialQuery,
      aiAreaSelectActive,
      aiAreaGeometry,
      startAiAreaSelect,
      stopAiAreaSelect,
      clearAiAreaGeometry,
      selectionResults,
      selectionIndex,
      setSelectionIndex,
      clearSelectionResults,
      deleteSelectionResults,
      objectCalculatorEnabled,
      toggleObjectCalculator,
      objectCalcActive,
      objectCalcItems,
      objectCalcTotals,
      startObjectCalc,
      stopObjectCalc,
      clearObjectCalc,
      drawActive,
      drawLayerId,
      drawError,
      startDraw,
      stopDraw,
      addObjectActive,
      startAddObject,
      stopAddObject,
      addObjectAtCoordinate,
      attributeFilters,
      setAttributeFilter,
      removeFeatureFromMap,
      coordinate,
      zoomLevel,
      scaleLabel,
      fitToLayers,
      fitToLayerExtent,
      flyToSearchHit,
      flyToCoordinates,
      getViewState,
      openTableLayer,
      openLayerTable,
      closeLayerTable,
      overlapPicker,
      selectOverlapItem,
      closeOverlapPicker,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      ready,
      layers,
      layerVisibility,
      layerOpacity,
      selectedFeature,
      selectedFeatureId,
      selectedFeatureLayerId,
      selectedFeaturePhotoFileId,
      selectedFeatureMetrics,
      activeBasemapId,
      locating,
      locateError,
      measureActive,
      measureMode,
      measureShape,
      boxSelectActive,
      spatialQueryActive,
      aiAreaSelectActive,
      aiAreaGeometry,
      selectionResults,
      selectionIndex,
      objectCalculatorEnabled,
      objectCalcActive,
      objectCalcItems,
      objectCalcTotals,
      drawActive,
      drawLayerId,
      drawError,
      addObjectActive,
      attributeFilters,
      coordinate,
      zoomLevel,
      scaleLabel,
      openTableLayer,
      overlapPicker,
    ],
  );

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
}
